import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  Avatar,
  Button,
  Grid2,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  IconCalendar,
  IconCash,
  IconCurrencyDollarOff,
  IconTrashX,
  IconUser,
  IconUserOff,
} from "@tabler/icons-react";

import StudentDetailsList from "../components/StudentDetailsList";
import ClassesFrequency from "../components/ClassesFrequency";
import ContainerLoading from "../components/ContainerLoading";

import PaymentModality from "../components/PaymentModality";
import StudentDelete from "../components/StudentDelete";
import StudentEnableOrDisable from "../components/StudentEnableOrDisable";
import ModalConfirm from "../components/ModalConfirm";
import GraduationList from "../components/GraduationList";
import StudentPlanSelect from "../components/StudentPlanSelect";

import { deleteStudentPlan, studentsListById } from "../services/students";
import StudentChangeDueDate from "../components/StudentChangeDueDate";
import BillingHistory from "../components/BillingHistory";
import PaymentCreateDrawer from "../components/PaymentCreateDrawer";

type StudentsDetailsProps = {
  id?: number;
};

const StudentsDetails = ({ id }: StudentsDetailsProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const [value, setValue] = useState("0");
  const [deleteAlert, setDeleteAlert] = useState(false);
  const [openSelectPlan, setOpenSelectPlan] = useState(false);
  const [openChangeDueDate, setOpenChangeDueDate] = useState(false);
  const [disableOrEnableAlert, setDisableOrEnableAlert] = useState(false);
  const [createPayment, setCreatePayment] = useState(false);
  const [cancelPlan, setCancelPlan] = useState(false);
  const studentId = id ?? Number(params?.id);

  const {
    data,
    isLoading,
    isFetching,
    refetch,
  }: {
    data: any;
    refetch: () => void;
    isLoading: boolean;
    isFetching: boolean;
  } = useQuery({
    queryKey: ["studentsListById", studentId],
    queryFn: async () => await studentsListById(studentId),
    enabled: !!studentId,
    initialData: { studentOnPlan: [] },
  });

  const { mutate: mutateCancelPlan, isPending: isPendingCancelPlan } =
    useMutation({
      mutationFn: async (id: number) => await deleteStudentPlan(id),
      onSuccess: () => {
        refetch();
        toast.success("Plano cancelado.");
      },
      onError: (e: { response: { data: { message: string } } }) => {
        toast.error(e.response.data.message || "Erro ao cancelar plano.");
      },
    });

  const handleChangeValue = (
    _event: React.SyntheticEvent,
    newValue: string
  ) => {
    setValue(newValue);
  };

  const onOpenSelectPlan = () => {
    setOpenSelectPlan(true);
  };

  const loading = isLoading || isFetching;
  const plan = data?.studentOnPlan ? data?.studentOnPlan[0] : null;

  return (
    <ContainerLoading loading={loading || isPendingCancelPlan}>
      <Grid2 container spacing={2} padding={2}>
        <Grid2 size={{ xs: 12, lg: 3 }}>
          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <Stack
                justifyContent={"center"}
                alignItems={"center"}
                sx={(theme) => ({
                  [theme.breakpoints.down("sm")]: {
                    marginTop: 7,
                  },
                })}
              >
                <Avatar
                  alt="student_avatar"
                  src={data?.user?.avatar}
                  sx={{
                    width: 125,
                    height: 125,
                    borderWidth: 4,
                    borderStyle: "solid",
                    borderColor: "primary.contrastText",
                  }}
                />
              </Stack>
            </Grid2>
            <Grid2 size={12}>
              <Typography
                variant="h2"
                fontWeight={600}
                fontSize={20}
                textAlign={"center"}
              >
                {data?.fullName}
              </Typography>
            </Grid2>
            <Grid2 size={12}>
              <Grid2 container spacing={1}>
                <Grid2 size={12}>
                  <Button
                    onClick={() => setCreatePayment(true)}
                    fullWidth
                    variant="contained"
                    color="primary"
                    endIcon={<IconCash />}
                    sx={{ justifyContent: "space-between" }}
                  >
                    Criar cobrança
                  </Button>
                </Grid2>
                <Grid2 size={12}>
                  <Button
                    onClick={() => setDisableOrEnableAlert(true)}
                    endIcon={
                      data?.status === "DISABLED" ? (
                        <IconUser />
                      ) : (
                        <IconUserOff />
                      )
                    }
                    fullWidth
                    variant="contained"
                    color="primary"
                    sx={{ justifyContent: "space-between" }}
                  >
                    {data?.status === "DISABLED" ? "Desbloquear" : "Bloquear"}
                  </Button>
                </Grid2>
                {plan && (
                  <Grid2 size={12}>
                    <Button
                      onClick={() => {
                        setOpenChangeDueDate(true);
                      }}
                      endIcon={<IconCalendar />}
                      fullWidth
                      variant="contained"
                      color="primary"
                      sx={{ justifyContent: "space-between" }}
                    >
                      Alterar vencimento
                    </Button>
                  </Grid2>
                )}
                <Grid2 size={12}>
                  <Button
                    onClick={onOpenSelectPlan}
                    endIcon={<IconCash />}
                    fullWidth
                    variant="contained"
                    color="primary"
                    sx={{ justifyContent: "space-between" }}
                  >
                    {plan ? "Trocar plano" : "Atribuir plano"}
                  </Button>
                </Grid2>{" "}
                {data.status !== "DISABLED" && plan && (
                  <Grid2 size={12}>
                    <Button
                      onClick={() => setCancelPlan(true)}
                      endIcon={<IconCurrencyDollarOff />}
                      fullWidth
                      variant="contained"
                      color="primary"
                      sx={{ justifyContent: "space-between" }}
                    >
                      Cancelar Plano{" "}
                    </Button>
                  </Grid2>
                )}
                <Grid2 size={12}>
                  <Button
                    onClick={() => setDeleteAlert(true)}
                    endIcon={<IconTrashX />}
                    fullWidth
                    variant="contained"
                    color="error"
                    sx={{ justifyContent: "space-between" }}
                  >
                    Excluir
                  </Button>
                </Grid2>
              </Grid2>
            </Grid2>
          </Grid2>
        </Grid2>
        <Grid2 size={{ xs: 12, lg: 9 }}>
          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <Tabs
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  marginBottom: 2,
                }}
                onChange={handleChangeValue}
                value={value}
                textColor="inherit"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="DADOS DO ALUNO" value="0" />
                <Tab label="FREQUÊNCIA" value="1" />
                <Tab label="FINANCEIRO" value="2" />
                <Tab label="GRADUAÇÃO" value="3" />
              </Tabs>
            </Grid2>
            {value === "0" && (
              <Grid2 size={12}>
                <StudentDetailsList data={data} refetch={refetch} />
              </Grid2>
            )}

            {value === "1" && (
              <Grid2 size={12}>
                <Grid2 container spacing={2}>
                  <Grid2 size={12}>
                    <ClassesFrequency studentId={Number(studentId)} />
                  </Grid2>
                </Grid2>
              </Grid2>
            )}
            {value === "2" && (
              <Grid2 size={12}>
                <Grid2 container spacing={2}>
                  <Grid2 size={12}>
                    <PaymentModality onOpen={onOpenSelectPlan} data={data} />
                  </Grid2>
                  <Grid2 size={12}>
                    <Stack spacing={2}>
                      <BillingHistory data={data.payment} refetch={refetch} />
                    </Stack>
                  </Grid2>
                </Grid2>
              </Grid2>
            )}

            {value === "3" && (
              <Grid2 size={12}>
                <Grid2 container spacing={2}>
                  <Grid2 size={12}>
                    <GraduationList
                      studentId={Number(studentId)}
                      refetch={() => refetch()}
                    />
                  </Grid2>
                </Grid2>
              </Grid2>
            )}
          </Grid2>
        </Grid2>
      </Grid2>

      {openSelectPlan && (
        <StudentPlanSelect
          open={openSelectPlan}
          id={Number(id)}
          planId={plan?.planId}
          onClose={() => setOpenSelectPlan(false)}
          onAction={() => {
            refetch();
            setTimeout(() => {
              setOpenSelectPlan(false);
            }, 1000);
          }}
        />
      )}

      {createPayment && (
        <PaymentCreateDrawer
          open={createPayment}
          studentId={data?.id}
          onClose={() => setCreatePayment(false)}
          refetch={refetch}
          onAction={() => {
            refetch();
          }}
        />
      )}

      {openChangeDueDate && (
        <StudentChangeDueDate
          open={openChangeDueDate}
          onClose={() => setOpenChangeDueDate(false)}
          onAction={() => {
            refetch();
          }}
          data={data}
        />
      )}

      <ModalConfirm
        title="Cancelar Plano"
        description="Tem certeza que, deseja cancelar o plano do seu aluno?"
        onClose={() => setCancelPlan(false)}
        onAction={() => {
          mutateCancelPlan(Number(studentId));
          setCancelPlan(false);
          // navigate("/students");
        }}
        open={cancelPlan}
        buttonTitle="Cancelar Plano"
        loading={isPendingCancelPlan}
      />
      <StudentDelete
        open={deleteAlert}
        onClose={() => setDeleteAlert(false)}
        onAction={() => navigate("/students")}
        id={Number(id)}
      />
      <StudentEnableOrDisable
        open={disableOrEnableAlert}
        onClose={() => setDisableOrEnableAlert(false)}
        onAction={() => navigate("/students")}
        id={Number(id)}
        status={data?.status}
      />
    </ContainerLoading>
  );
};

export default StudentsDetails;
